export const WebsiteIdentifier = {
  earthcomposting: 6
} as const;

export type WebsiteIdentifier = typeof WebsiteIdentifier[keyof typeof WebsiteIdentifier];

export interface PageTrackingRequest {
  id: number;
  pageName: string;
  sessionId: string | null;
  userId: string | null;
  websiteId: WebsiteIdentifier;
  duration?: number;
  additionalData?: string;
}

// Page Tracking Utility
let trackingIdCounter = 1;
const trackedPages = new Set<string>();

export const createPageTrackingRequest = (
  pageName: string,
  sessionId: string | null = null,
  userId: string | null = null,
  duration?: number,
  additionalData?: string
): PageTrackingRequest => {
  const websiteId: WebsiteIdentifier = WebsiteIdentifier.earthcomposting;

  return {
    id: trackingIdCounter++,
    pageName,
    sessionId,
    userId,
    websiteId,
    duration,
    additionalData
  };
};

import Config from "../Config";
export const trackPageView = async (
  pageName: string,
  sessionId: string | null = null,
  userId: string | null = null,
  duration?: number,
  additionalData?: string
): Promise<boolean> => {
  // Create a unique key for this page view
  const trackingKey = `${pageName}-${sessionId}-${Date.now()}`;
  
  // Check if we've already tracked this exact page view
  if (trackedPages.has(trackingKey)) {
    return true;
  }

  try {
    const trackingRequest = createPageTrackingRequest(
      pageName,
      sessionId,
      userId,
      duration,
      additionalData
    );

    // Mark this page as tracked before making the API call
    trackedPages.add(trackingKey);

    // Send tracking data to API endpoint
    const response = await fetch(`${Config.apiUrl}/PageTracking/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(trackingRequest)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Also store locally as backup
    const existingData = localStorage.getItem('pageTracking');
    const trackingData = existingData ? JSON.parse(existingData) : [];
    trackingData.push({
      ...trackingRequest,
      timestamp: new Date().toISOString(),
      sent: true
    });
    localStorage.setItem('pageTracking', JSON.stringify(trackingData));

    return true;
  } catch (error) {
    console.error('Page tracking error:', error);
    
    // Remove from tracked pages if API call failed
    trackedPages.delete(trackingKey);
    
    // Fallback: store locally if API call fails
    try {
      const existingData = localStorage.getItem('pageTracking');
      const trackingData = existingData ? JSON.parse(existingData) : [];
      const trackingRequest = createPageTrackingRequest(
        pageName,
        sessionId,
        userId,
        duration,
        additionalData
      );
      trackingData.push({
        ...trackingRequest,
        timestamp: new Date().toISOString(),
        sent: false
      });
      localStorage.setItem('pageTracking', JSON.stringify(trackingData));
    } catch (localError) {
      console.error('Failed to store tracking data locally:', localError);
    }
    
    return false;
  }
};

export const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

export const getUserId = (): string | null => {
  // This would typically come from your authentication system
  return localStorage.getItem('userId');
};

export const clearTrackingCache = (): void => {
  trackedPages.clear();
};

export const retryFailedTracking = async (): Promise<number> => {
  try {
    const existingData = localStorage.getItem('pageTracking');
    if (!existingData) return 0;

    const trackingData = JSON.parse(existingData);
    const failedRequests = trackingData.filter((item: any) => !item.sent);
    
    if (failedRequests.length === 0) return 0;

    // Remove timestamp and sent fields for API request
    const cleanedRequests = failedRequests.map(({ timestamp, sent, ...request }: any) => request);

    const response = await fetch(`${Config.apiUrl}/PageTracking/batch-track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cleanedRequests)
    });

    if (response.ok) {
      // Mark failed requests as sent
      const updatedData = trackingData.map((item: any) => 
        !item.sent ? { ...item, sent: true } : item
      );
      localStorage.setItem('pageTracking', JSON.stringify(updatedData));
      return failedRequests.length;
    }

    return 0;
  } catch (error) {
    console.error('Failed to retry tracking requests:', error);
    return 0;
  }
};

// Initialize page tracking system
export const initializePageTracking = (): void => {
  // Retry failed tracking requests when the app starts
  retryFailedTracking().then((retriedCount) => {
    if (retriedCount > 0) {
      console.log(`Successfully retried ${retriedCount} failed tracking requests`);
    }
  });

  // Set up periodic retry of failed requests (every 30 seconds)
  setInterval(() => {
    retryFailedTracking();
  }, 30000);

  // Set up cleanup of old tracking data (keep last 7 days)
  const cleanupOldData = () => {
    try {
      const existingData = localStorage.getItem('pageTracking');
      if (!existingData) return;

      const trackingData = JSON.parse(existingData);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const filteredData = trackingData.filter((item: any) => {
        const itemDate = new Date(item.timestamp);
        return itemDate > sevenDaysAgo;
      });

      localStorage.setItem('pageTracking', JSON.stringify(filteredData));
    } catch (error) {
      console.error('Failed to cleanup old tracking data:', error);
    }
  };

  // Cleanup old data on initialization and then daily
  cleanupOldData();
  setInterval(cleanupOldData, 24 * 60 * 60 * 1000); // 24 hours
};
